import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface KioskInactivityTimeoutOverride {
    idleSeconds: number;
    modalSeconds?: number;
}

interface KioskIdleTimerOverrideContextValue {
    activeOverride: KioskInactivityTimeoutOverride | null;
    registerOverride: (override: KioskInactivityTimeoutOverride) => () => void;
}

const KioskIdleTimerOverrideContext = createContext<KioskIdleTimerOverrideContextValue | null>(null);

export const KioskIdleTimerOverrideProvider = (props: { children: React.ReactNode }) => {
    const [stack, setStack] = useState<Array<{ token: symbol; override: KioskInactivityTimeoutOverride }>>([]);

    const registerOverride = useCallback((override: KioskInactivityTimeoutOverride) => {
        const token = Symbol();
        setStack((current) => [...current, { token, override }]);
        return () => setStack((current) => current.filter((entry) => entry.token !== token));
    }, []);

    const activeOverride = useMemo(() => stack.at(-1)?.override ?? null, [stack]);

    return <KioskIdleTimerOverrideContext.Provider value={{ activeOverride, registerOverride }}>{props.children}</KioskIdleTimerOverrideContext.Provider>;
};

/**
 * Returns a function to temporarily override the kiosk inactivity timeout.
 * The returned cleanup function removes the override (call it from useEffect cleanup).
 */
export const useKioskInactivityTimeoutOverride = (): ((override: KioskInactivityTimeoutOverride) => () => void) => {
    const context = useContext(KioskIdleTimerOverrideContext);
    if (!context) {
        throw new Error('useKioskInactivityTimeoutOverride must be used within a KioskIdleTimerOverrideProvider');
    }
    return context.registerOverride;
};

export const useActiveKioskInactivityTimeoutOverride = (): KioskInactivityTimeoutOverride | null => {
    const context = useContext(KioskIdleTimerOverrideContext);
    if (!context) {
        throw new Error('useActiveKioskInactivityTimeoutOverride must be used within a KioskIdleTimerOverrideProvider');
    }
    return context.activeOverride;
};
